/**
 * InteractiveGuide — the catalog browser with three switchable layouts:
 * Cards, Compact list, and the horizontal Mind map. Clicking a coral in any
 * layout opens the editable detail pop-up (handled by the parent via
 * `onSelectCoral`); the mind map additionally lets color/growth nodes drive
 * the shared filters.
 */
import { useMemo } from 'react';

import { ColorDots, GrowthPill } from './chips';
import { CoralThumb } from './CoralThumb';
import { cleanText } from './catalog';
import { MindMap } from './MindMap';
import { buildMindMapLayout } from './mindmapLayout';
import type {
  Coral,
  ColorTag,
  GrowthForm,
  GuideLayout,
} from './types';

export interface InteractiveGuideProps {
  readonly corals: readonly Coral[];
  readonly layout: GuideLayout;
  readonly onSelectCoral: (coralId: string) => void;
  readonly onColorFilter: (color: ColorTag) => void;
  readonly onGrowthFilter: (form: GrowthForm) => void;
}

function CardsView({
  corals,
  onSelectCoral,
}: {
  readonly corals: readonly Coral[];
  readonly onSelectCoral: (id: string) => void;
}) {
  if (corals.length === 0) {
    return (
      <p className="py-12 text-center text-sm text-text-3">
        No corals match the current filters.
      </p>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {corals.map((coral) => (
        <button
          key={coral.id}
          type="button"
          onClick={() => onSelectCoral(coral.id)}
          className="group overflow-hidden rounded-xl bg-bg-elevated text-left ring-1 ring-surface-stroke transition-shadow hover:ring-accent/50"
        >
          <div className="aspect-[4/3] w-full overflow-hidden bg-bg-mid">
            <CoralThumb
              coral={coral}
              prefer="colony"
              className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
            />
          </div>
          <div className="space-y-1.5 p-3">
            <p className="line-clamp-1 text-sm font-semibold text-text-1">
              {coral.name}
            </p>
            <p className="line-clamp-1 text-xs italic text-text-3">
              {coral.species}
            </p>
            <div className="flex items-center justify-between gap-2 pt-1">
              <ColorDots colors={coral.colors} />
              <GrowthPill form={coral.growthForm} />
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function ListView({
  corals,
  onSelectCoral,
}: {
  readonly corals: readonly Coral[];
  readonly onSelectCoral: (id: string) => void;
}) {
  if (corals.length === 0) {
    return (
      <p className="py-12 text-center text-sm text-text-3">
        No corals match the current filters.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-surface-stroke overflow-hidden rounded-xl ring-1 ring-surface-stroke">
      {corals.map((coral) => (
        <li key={coral.id}>
          <button
            type="button"
            onClick={() => onSelectCoral(coral.id)}
            className="flex w-full items-center gap-3 bg-bg-elevated px-3 py-2 text-left transition-colors hover:bg-surface-glass"
          >
            <CoralThumb
              coral={coral}
              prefer="frag"
              className="h-10 w-10 flex-shrink-0 rounded-md object-cover"
            />
            <div className="min-w-0 flex-1">
              <p className="line-clamp-1 text-sm font-medium text-text-1">
                {coral.name}
              </p>
              <p className="line-clamp-1 text-xs text-text-3">
                {cleanText(coral.species)}
              </p>
            </div>
            <ColorDots colors={coral.colors} />
            <GrowthPill form={coral.growthForm} />
          </button>
        </li>
      ))}
    </ul>
  );
}

export function InteractiveGuide({
  corals,
  layout,
  onSelectCoral,
  onColorFilter,
  onGrowthFilter,
}: InteractiveGuideProps) {
  const mindMapLayout = useMemo(() => buildMindMapLayout(corals), [corals]);

  if (layout === 'mindmap') {
    return (
      <MindMap
        layout={mindMapLayout}
        onColorClick={onColorFilter}
        onGrowthClick={onGrowthFilter}
        onCoralClick={onSelectCoral}
      />
    );
  }

  return (
    <div className="h-full overflow-y-auto pr-1">
      {layout === 'cards' ? (
        <CardsView corals={corals} onSelectCoral={onSelectCoral} />
      ) : (
        <ListView corals={corals} onSelectCoral={onSelectCoral} />
      )}
    </div>
  );
}
